import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Save, Trash2, Pencil, X, Plus, Target } from "lucide-react";
import Seo from "@/components/Seo";
import { api, getToken, clearToken, fmtErr } from "@/lib/api";

// Convert YYYY-MM-DD -> DD/MM/YYYY
const toDMY = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
};
// DD/MM/YYYY -> YYYY-MM-DD
const toISO = (dmy) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/");
  return `${y}-${m}-${d}`;
};

function ResultForm({ initial, onSaved }) {
  const [dateIso, setDateIso] = useState(initial ? toISO(initial.date) : "");
  const [fr, setFr] = useState(initial?.fr || "");
  const [sr, setSr] = useState(initial?.sr || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!initial && !dateIso) {
      const t = new Date();
      setDateIso(t.toISOString().slice(0, 10));
    }
  }, [initial, dateIso]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { date: toDMY(dateIso), fr: fr || null, sr: sr || null };
      if (initial?.id) {
        await api.put(`/results/${initial.id}`, body);
        toast.success("Result updated");
      } else {
        await api.post("/results", body);
        toast.success("Result published");
      }
      setFr("");
      setSr("");
      onSaved();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "mt-1 w-full rounded-lg border border-black/15 bg-white px-3 py-2 focus:outline-none focus:ring-2 focus:ring-[#F04E1E]/40 focus:border-[#F04E1E]";

  return (
    <form
      onSubmit={submit}
      className="grid grid-cols-1 sm:grid-cols-4 gap-3"
      data-testid="result-form"
    >
      <div>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-500">
          Date
        </label>
        <input
          type="date"
          value={dateIso}
          onChange={(e) => setDateIso(e.target.value)}
          required
          className={inputCls}
          data-testid="result-date-input"
        />
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-500">
          F/R (10:30 AM)
        </label>
        <input
          value={fr}
          onChange={(e) => setFr(e.target.value)}
          maxLength={20}
          placeholder="e.g. 07 or XX"
          className={`${inputCls} num-font`}
          data-testid="result-fr-input"
        />
      </div>
      <div>
        <label className="text-xs font-bold uppercase tracking-widest text-slate-500">
          S/R (11:30 AM)
        </label>
        <input
          value={sr}
          onChange={(e) => setSr(e.target.value)}
          maxLength={20}
          placeholder="e.g. 42 or XX"
          className={`${inputCls} num-font`}
          data-testid="result-sr-input"
        />
      </div>
      <div className="flex items-end">
        <button
          type="submit"
          disabled={busy}
          className="w-full rounded-lg bg-[#F04E1E] text-white py-2.5 font-bold hover:bg-[#D63E10] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2 shadow-[0_6px_14px_-6px_rgba(240,78,30,0.55)]"
          data-testid="result-submit"
        >
          <Save className="w-4 h-4" />
          {initial ? "Update" : "Publish"}
        </button>
      </div>
    </form>
  );
}

function ResultsPanel() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const r = await api.get("/results?page=1&limit=100");
    setItems(r.data.items);
  };
  useEffect(() => {
    load();
  }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this result?")) return;
    try {
      await api.delete(`/results/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  return (
    <section className="rounded-2xl bg-white border border-black/5 p-5 shadow-[0_10px_24px_-14px_rgba(0,0,0,0.15)]">
      <h2 className="font-display text-xl font-black text-black mb-4 flex items-center gap-2">
        <span className="w-8 h-8 rounded-lg bg-[#F04E1E]/10 grid place-items-center">
          <Plus className="w-5 h-5 text-[#F04E1E]" />
        </span>
        {editing ? "Edit Result" : "Publish / Update Result"}
      </h2>
      <ResultForm
        key={editing?.id || "new"}
        initial={editing}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
      {editing && (
        <button
          onClick={() => setEditing(null)}
          className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1"
          data-testid="result-cancel-edit"
        >
          <X className="w-3.5 h-3.5" /> Cancel edit
        </button>
      )}

      <h3 className="font-display font-black text-slate-700 mt-8 mb-2">
        Published Results
      </h3>
      <div className="rounded-xl border border-black/10 overflow-hidden">
        <div className="grid grid-cols-[1.2fr_1fr_1fr_1.2fr] bg-[#F04E1E] text-white text-xs uppercase tracking-widest font-bold px-3 py-2.5">
          <span>Date</span>
          <span className="text-center">F/R</span>
          <span className="text-center">S/R</span>
          <span className="text-right">Actions</span>
        </div>
        {items.length === 0 && (
          <div className="p-4 text-sm text-slate-500 text-center">
            No results yet.
          </div>
        )}
        {items.map((it, i) => (
          <div
            key={it.id}
            className={`grid grid-cols-[1.2fr_1fr_1fr_1.2fr] px-3 py-2.5 items-center border-t border-black/5 ${
              i % 2 ? "bg-[#FAF7F2]" : "bg-white"
            }`}
          >
            <span className="text-sm font-semibold text-slate-800">
              {it.date}
            </span>
            <span className="num-font text-center font-black text-black">
              {it.fr ?? "—"}
            </span>
            <span className="num-font text-center font-black text-black">
              {it.sr ?? "—"}
            </span>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setEditing(it)}
                className="p-1.5 rounded-lg hover:bg-[#F04E1E]/10 text-[#F04E1E]"
                title="Edit"
                data-testid={`result-edit-${it.date_key}`}
              >
                <Pencil className="w-4 h-4" />
              </button>
              <button
                onClick={() => del(it.id)}
                className="p-1.5 rounded-lg hover:bg-red-50 text-red-600 inline-flex items-center gap-1"
                data-testid={`result-delete-${it.date_key}`}
                title="Delete"
                aria-label="Delete result"
              >
                <Trash2 className="w-4 h-4" />
                <span className="sr-only">Delete</span>
              </button>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function TargetLogo({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true">
      <circle cx="32" cy="32" r="30" fill="#fff" stroke="#111" strokeWidth="3" />
      <circle cx="32" cy="32" r="22" fill="#fff" stroke="#111" strokeWidth="3" />
      <circle cx="32" cy="32" r="14" fill="#F04E1E" stroke="#111" strokeWidth="3" />
      <circle cx="32" cy="32" r="5" fill="#111" />
      <path d="M6 6 L34 34" stroke="#4A2E14" strokeWidth="5" strokeLinecap="round" />
      <polygon points="6,6 14,6 6,14" fill="#F04E1E" />
    </svg>
  );
}

export default function AdminDashboard() {
  const nav = useNavigate();

  useEffect(() => {
    if (!getToken()) {
      nav("/admin");
      return;
    }
    api.get("/auth/me").catch(() => {
      clearToken();
      nav("/admin");
    });
  }, [nav]);

  const logout = () => {
    clearToken();
    nav("/admin");
  };

  return (
    <>
      <Seo
        title="Admin Dashboard | Juwai Morning Teer"
        description="Admin dashboard"
        path="/admin/dashboard"
        noindex
      />
      <div className="min-h-screen bg-[#FAF7F2]">
        <header
          className="bg-white border-b border-black/5 sticky top-0 z-40"
          data-testid="admin-header"
        >
          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <TargetLogo className="w-9 h-9 shrink-0" />
              <div className="min-w-0">
                <div className="font-display font-black leading-none text-lg sm:text-xl truncate">
                  <span className="text-black">JUWAI</span>{" "}
                  <span className="text-[#F04E1E]">MORNING TEER</span>
                </div>
                <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mt-0.5">
                  Admin Panel
                </div>
              </div>
            </div>
            <button
              onClick={logout}
              className="inline-flex items-center gap-2 rounded-lg bg-black text-white hover:bg-neutral-800 px-3 py-2 text-sm font-semibold transition-colors shrink-0"
              data-testid="admin-logout"
            >
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
          <ResultsPanel />
        </main>
      </div>
    </>
  );
}
